import React, { useState, useMemo, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  useTheme,
  IconButton,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import { DataGrid, useGridApiRef, gridFilteredSortedRowIdsSelector } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { buildChildTradeRow, getFundFilterValue, getStrategyFilterValue } from "./tradeBlotterHelpers";
import {
  isChildGridRow,
  isNonSelectableChildRow,
  buildTopLevelDisplayNumberMap,
  getStateColor,
} from "./tradeBlotterGridUtils";

const TradeBlotterTable = forwardRef(({
  data,
  onFilterStateChange,
  onColumnStateChange,
  onSelectionChange,
  viewType = "non-deleted",
  routeList,
  flatChildView = false,
}, ref) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const gridApiRef = useGridApiRef();
  const [filterKey, setFilterKey] = useState(0);
  const [gridFilterModel, setGridFilterModel] = useState({ items: [] });
  const [displayNumberByRowId, setDisplayNumberByRowId] = useState(() => new Map());
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 200 });
  // Controlled sort — DataGrid must not sort the flat expanded list itself
  const [sortModel, setSortModel] = useState([]);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Expose DataGrid methods to parent component
  useImperativeHandle(ref, () => ({
    toggleFilterPanel: () => gridApiRef.current?.showFilterPanel(),
    clearSelection: () => {
      setRowSelectionModel({ type: "include", ids: new Set() });
      onSelectionChange?.([]);
    },
  }), [gridApiRef, onSelectionChange]);

  const handleSelectionChange = (newSelection) => {
    // MUI X Data Grid v8 passes { type: 'include'|'exclude', ids: Set }
    if (newSelection && typeof newSelection === 'object') {

      let idsArray = [];
      const allRowIds = rowsWithExpandInfo.map(row => row.id);
      
      if (newSelection.type === 'exclude') {
        const excludedIds = newSelection.ids instanceof Set
          ? newSelection.ids
          : new Set(Array.isArray(newSelection.ids) ? newSelection.ids : []);
        idsArray = allRowIds.filter(id => !excludedIds.has(id));
      } else {
        idsArray = newSelection.ids instanceof Set
          ? Array.from(newSelection.ids)
          : Array.isArray(newSelection.ids)
            ? newSelection.ids
            : [];
      }
      
      setRowSelectionModel(newSelection);
      
      if (onSelectionChange) {
        onSelectionChange(idsArray);
      }
    }
  };

  const handleFilterModelChange = (model) => {
    setGridFilterModel(model);
    // Force re-render of cells when filters change
    setFilterKey(prev => prev + 1);
    
    if (onFilterStateChange) {
      const hasColumnFilters =
        model.items && model.items.length > 0;
      const hasQuickFilter =
        model.quickFilterValues?.some((value) => String(value).trim());
      onFilterStateChange(Boolean(hasColumnFilters || hasQuickFilter));
    }

    requestAnimationFrame(() => refreshDisplayNumbers());
  };

  useEffect(() => {
    setGridFilterModel({ items: [] });
    setSortModel([]);
    setFilterKey((prev) => prev + 1);
    if (onFilterStateChange) {
      onFilterStateChange(false);
    }
  }, [flatChildView, onFilterStateChange]);

  const rowsWithExpandInfo = useMemo(() => data, [data]);

  const getRowSortValue = useCallback((row, field) => {
    if (!row || !field) return undefined;
    if (field === "fund") return getFundFilterValue(row);
    if (field === "strategy") return getStrategyFilterValue(row);
    return row?.[field];
  }, []);

  const compareSortValues = useCallback((a, b) => {
    if (a == null && b == null) return 0;
    if (a == null || a === "") return -1;
    if (b == null || b === "") return 1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    const aNum =
      typeof a === "string" && a.trim() !== "" && !Number.isNaN(Number(a))
        ? Number(a)
        : null;
    const bNum =
      typeof b === "string" && b.trim() !== "" && !Number.isNaN(Number(b))
        ? Number(b)
        : null;
    if (aNum != null && bNum != null) return aNum - bNum;
    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }, []);

  const sortRowsByModel = useCallback(
    (rows, model) => {
      if (!model?.length) return rows;
      const sorted = [...rows];
      [...model].reverse().forEach(({ field, sort }) => {
        if (!field || !sort) return;
        const dir = sort === "desc" ? -1 : 1;
        sorted.sort(
          (a, b) =>
            compareSortValues(getRowSortValue(a, field), getRowSortValue(b, field)) *
            dir
        );
      });
      return sorted;
    },
    [compareSortValues, getRowSortValue]
  );

  /**
   * Parent view: sort only top-level P/S rows, then re-flatten with expanded
   * children under each parent. Stamp __treeOrder so grid sort can only
   * follow this parent→child sequence.
   */
  const flattenedRows = useMemo(() => {
    // Never treat already-materialized child rows as top-level parents
    const topLevelSource = (rowsWithExpandInfo || []).filter(
      (row) =>
        !row?.isChildRow &&
        row?.distributionType !== "C" &&
        !String(row?.id ?? "").startsWith("child::")
    );

    let list;
    if (flatChildView) {
      list = sortRowsByModel(rowsWithExpandInfo, sortModel);
    } else {
      const topLevel = sortRowsByModel(topLevelSource, sortModel);
      list = [];
      topLevel.forEach((row) => {
        list.push(row);
        const rowId = row.id;
        const isExpanded =
          expandedRows.has(rowId) || expandedRows.has(String(rowId));
        if (
          row.distributionType === "P" &&
          isExpanded &&
          row.childTrades?.length
        ) {
          row.childTrades.forEach((childTrade, childIndex) => {
            list.push(buildChildTradeRow(row, childTrade, childIndex));
          });
        }
      });
    }

    const withOrder = list; // avoid cloning every row; order map is built separately below

    if (process.env.NODE_ENV !== "production") {
      const seen = new Set();
      withOrder.forEach((row) => {
        const id = String(row.id);
        if (seen.has(id)) {
          console.warn("[TradeBlotter] Duplicate grid row id:", id);
        }
        seen.add(id);
      });
    }

    return withOrder;
  }, [
    rowsWithExpandInfo,
    expandedRows,
    flatChildView,
    sortModel,
    sortRowsByModel,
  ]);

  useEffect(() => {
    if (flatChildView) {
      setExpandedRows(new Set());
    }
  }, [flatChildView]);

  const flattenedRowById = useMemo(() => {
    const map = new Map();
    flattenedRows.forEach((row) => {
      map.set(row.id, row);
      map.set(String(row.id), row);
    });
    return map;
  }, [flattenedRows]);

  const treeOrderById = useMemo(() => {
    const map = new Map();
    flattenedRows.forEach((row, index) => {
      map.set(String(row.id), index);
    });
    return map;
  }, [flattenedRows]);

  // Keep sort comparator / cell readers stable so DataGrid columns are not
  // rebuilt on every websocket trades update (that was freezing navigation).
  const treeOrderByIdRef = useRef(treeOrderById);
  treeOrderByIdRef.current = treeOrderById;
  const displayNumberByRowIdRef = useRef(displayNumberByRowId);
  displayNumberByRowIdRef.current = displayNumberByRowId;
  const expandedRowsRef = useRef(expandedRows);
  expandedRowsRef.current = expandedRows;

  const getTreeOrderSortComparator = useCallback(
    () => (v1, v2, cellParams1, cellParams2) => {
      const map = treeOrderByIdRef.current;
      const o1 =
        map.get(String(cellParams1?.id)) ?? Number.MAX_SAFE_INTEGER;
      const o2 =
        map.get(String(cellParams2?.id)) ?? Number.MAX_SAFE_INTEGER;
      return o1 - o2;
    },
    []
  );

  const refreshDisplayNumbers = useCallback(() => {
    if (aliveRef.current === false) return;
    const apiRef = gridApiRef;
    const getRow = (id) => {
      const normalizedId = String(id);
      return (
        flattenedRowById.get(normalizedId) ??
        flattenedRowById.get(id)
      );
    };

    let rowIds = flattenedRows.map((row) => row.id);
    if (apiRef?.current && !flatChildView) {
      try {
        const filteredIds = gridFilteredSortedRowIdsSelector(apiRef);
        if (filteredIds?.length) {
          const allowed = new Set(filteredIds.map(String));
          rowIds = flattenedRows
            .map((row) => row.id)
            .filter((id) => allowed.has(String(id)));
        }
      } catch {
        // Fall back to flattened row order when selector is unavailable.
      }
    } else if (apiRef?.current) {
      try {
        rowIds = gridFilteredSortedRowIdsSelector(apiRef);
      } catch {
        // Fall back to flattened row order when selector is unavailable.
      }
    }

    if (aliveRef.current === false) return;
    setDisplayNumberByRowId(
      buildTopLevelDisplayNumberMap(rowIds, getRow, flatChildView)
    );
  }, [gridApiRef, flattenedRowById, flattenedRows, flatChildView]);

  useEffect(() => {
    refreshDisplayNumbers();
  }, [flattenedRows, filterKey, flatChildView, refreshDisplayNumbers]);

  useEffect(() => {
    const api = gridApiRef?.current;
    if (!api?.subscribeEvent) return undefined;

    const unsubscribeSort = api.subscribeEvent("sortModelChange", () => {
      requestAnimationFrame(() => refreshDisplayNumbers());
    });
    const unsubscribeRows = api.subscribeEvent("rowsSet", () => {
      requestAnimationFrame(() => refreshDisplayNumbers());
    });
    const unsubscribeFilter = api.subscribeEvent("filterModelChange", () => {
      requestAnimationFrame(() => refreshDisplayNumbers());
    });
    const unsubscribeFilteredRows = api.subscribeEvent("filteredRowsSet", () => {
      requestAnimationFrame(() => refreshDisplayNumbers());
    });

    refreshDisplayNumbers();

    return () => {
      unsubscribeSort();
      unsubscribeRows();
      unsubscribeFilter();
      unsubscribeFilteredRows();
    };
  }, [gridApiRef, refreshDisplayNumbers]);

  const handleSortModelChange = useCallback(
    (model) => {
      setSortModel(model || []);
      requestAnimationFrame(() => refreshDisplayNumbers());
    },
    [refreshDisplayNumbers]
  );

  const toggleRowExpand = (rowId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      const key = rowId;
      const alt = String(rowId);
      if (next.has(key) || next.has(alt)) {
        next.delete(key);
        next.delete(alt);
      } else {
        next.add(key);
      }
      return next;
    });
    setFilterKey((prev) => prev + 1);
  };

  const columns = useMemo(() => {
    const defs = [
    {
      field: "__expand",
      headerName: "",
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (flatChildView) return null;
        if (isNonSelectableChildRow(params.row, params.id)) return null;
        if (params.row.distributionType !== "P") return null;
        const isExpanded =
          expandedRowsRef.current.has(params.id) ||
          expandedRowsRef.current.has(String(params.id));
        return (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              toggleRowExpand(params.id);
            }}
            sx={{ p: 0.25 }}
          >
            {isExpanded ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
         {
       field: "displayNumber",
       headerName: "#",
       flex: 0,
       width: Math.max(42, String(Math.max(flattenedRows.length, 1)).length * 9 + 12),
       minWidth: 42,
       sortable: false,
       filterable: false,
       disableColumnMenu: true,
       renderCell: (params) => {
         if (isChildGridRow(params.row, params.id, flatChildView)) {
           return null;
         }
         const displayNumber = displayNumberByRowIdRef.current.get(String(params.id));
         return (
           <Typography variant="body2" sx={{ fontWeight: 500 }}>
             {displayNumber ?? ""}
           </Typography>
         );
       },
     },
    {
      field: "timestamp",
      headerName: "Timestamp",
      flex: 0.6,
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 400, fontFamily: 'monospace' }}>
          {params.value || "00:00:00"}
        </Typography>
      ),
    },
    {
      field: "distributionType",
      headerName: "Type",
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => {
        const label = params.value;
        return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Chip
            label={label}
            color={
              label === "P" ? "primary" :
              label === "C" ? "secondary" :
              "default"
            }
            size="small"
            variant={label === "S" ? "outlined" : "filled"}
            sx={{
              fontWeight: 400,
              fontSize: "0.75rem",
              minWidth: "32px",
              height: "24px",
              borderRadius: "0px",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        </Box>
        );
      },
    },
    {
      field: "ticker",
      headerName: "Ticker",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },

    {
      field: "fund",
      headerName: "Fund",
      flex: 1.5,
      minWidth: 180,
      valueGetter: (value, row) => getFundFilterValue(row),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.row.fund}
        </Typography>
      ),
    },
    {
      field: "strategy",
      headerName: "Strategy",
      flex: 1.2,
      minWidth: 150,
      valueGetter: (value, row) => getStrategyFilterValue(row),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.row.strategy}
        </Typography>
      ),
    },
         {
       field: "state",
       headerName: "State",
       flex: 0.7,
       minWidth: 90,
       renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStateColor(params.value)}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      ),
    },
    {
      field: "side",
      headerName: "Trade Type",
      flex: 1.2,
      minWidth: 140,
      renderCell: (params) => {
        const isOpeningPosition = params.row.isOpeningPosition;
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              maxHeight: "20px",
              color: isOpeningPosition
                ? theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32'
                : theme.palette.mode === 'dark' ? '#e57373' : '#c62828',
              border: `1px solid ${isOpeningPosition
                ? theme.palette.mode === 'dark' ? '#66bb6a' : '#c8e6c9'
                : theme.palette.mode === 'dark' ? '#ef5350' : '#ffcdd2'}`,
            }}
          />
        );
      },
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
          {params.value?.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: "orderType",
      headerName: "Order Type",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "pricePerShare",
      headerName: "Price / Share",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: "monospace" }}>
          ${params.value?.toFixed(2)}
        </Typography>
      ),
    },
    {
      field: "commPerShare",
      headerName: "Comm / Share",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: "monospace" }}>
          ${params.value}
        </Typography>
      ),
    },
    {
      field: "custodian",
      headerName: "Custodian",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "broker",
      headerName: "Broker",
      flex: 1.2,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "routing",
      headerName: "Routing",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const isAuto = params.value === 0 || params.value === "0";
        return (
          <Chip
            label={isAuto ? "Auto" : "Manual"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              height: "22px",
              color: isAuto
                ? theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'
                : theme.palette.mode === 'dark' ? '#ffb74d' : '#e65100',
              border: `1px solid ${isAuto 
                ? theme.palette.mode === 'dark' ? '#42a5f5' : '#bbdefb'
                : theme.palette.mode === 'dark' ? '#ff9800' : '#ffe0b2'}`,
              backgroundColor: "transparent",
            }}
          />
        );
      },
    },
    {
      field: "routable",
      headerName: "Routable",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => {
        const isRoutable = params.value === "Yes";
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              height: "22px",
              color: isRoutable
                ? theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32'
                : theme.palette.mode === 'dark' ? '#e57373' : '#c62828',
              border: `1px solid ${isRoutable 
                ? theme.palette.mode === 'dark' ? '#66bb6a' : '#c8e6c9'
                : theme.palette.mode === 'dark' ? '#ef5350' : '#ffcdd2'}`,
              backgroundColor: "transparent",
            }}
          />
        );
      },
    },
    {
      field: "routeStatus",
      headerName: "Status",
      flex: 0.55,
      minWidth: 108,
      maxWidth: 158,
      renderCell: (params) => {
        const cs = params.row?.cancellationStatus;
        if (cs === 0) {
          return (
            <Chip
              label="Cancel"
              size="small"
              color="error"
              variant="filled"
              sx={{
              fontWeight: 600,
              fontSize: "0.7rem",
              height: "22px",
              maxWidth: "100%",
              "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
            }}
          />
          );
        }
        if (cs === 2) {
          return (
            <Chip
              label="Pending"
              size="small"
              color="warning"
              variant="filled"
              sx={{
                fontWeight: 600,
                fontSize: "0.7rem",
                height: "22px",
                maxWidth: "100%",
                "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          );
        }

        const v = params.value || "Not Routed";
        const color =
          v === "Filled"
            ? "success"
            : v === "Partially Filled"
              ? "warning"
              : v === "Routed"
                ? "info"
                : "default";
        return (
          <Chip
            label={v}
            size="small"
            color={color}
            variant={v === "Not Routed" ? "outlined" : "filled"}
            sx={{
              fontWeight: 600,
              fontSize: "0.7rem",
              height: "22px",
              maxWidth: "100%",
              "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
            }}
          />
        );
      },
    },
    {
      field: "routerDisplay",
      headerName: "Router",
      flex: 0.9,
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || ""}
        </Typography>
      ),
    },
    {
      field: "allocation",
      headerName: "Allocation",
      flex: 0.9,
      minWidth: 120,
      renderCell: (params) => {
        const hasAllocation =
          params?.row?.allocation &&
          typeof params.row.allocation === "object" &&
          Object.keys(params.row.allocation).length > 0;
        return hasAllocation ? (
          <Chip
            label="Allocated"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
          />
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {""}
          </Typography>
        );
      },
    },
    {
      field: "filledPercent",
      headerName: "Filled %",
      flex: 1,
      minWidth: 120,
      cellClassName: "filled-percent-cell",
      renderCell: (params) => {
        const filledShares = params.row.filledShares || 0;
        const totalShares = params.row.quantity || 0;
        const tooltipText = `${filledShares.toLocaleString()} / ${totalShares.toLocaleString()} shares filled`;

        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <Box
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "help",
                overflow: "hidden",
                borderRadius: "1px",
                alignSelf: "stretch",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#424242" : "#e0e0e0",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${Math.min(Math.max(params.value || 0, 0), 100)}%`,
                  backgroundColor:
                    (params.value || 0) >= 100
                      ? theme.palette.success.main
                      : "rgb(16 168 251 / 64%)",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  textAlign: "center",
                  position: "relative",
                  zIndex: 1,
                  lineHeight: 1,
                  color:
                    (params.value || 0) >= 100
                      ? "#fff"
                      : theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.7)"
                        : theme.palette.text.primary,
                }}
              >
                {params.value?.toFixed(2)}%
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "options",
      headerName: "Options",
      flex: 0.32,
      minWidth: 72,
      maxWidth: 96,
      renderCell: (params) => {
        const label = params?.row?.options;
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {label || ""}
          </Typography>
        );
      },
    },
    {
      field: "strikePrice",
      headerName: "Strike Price",
      flex: 0.44,
      minWidth: 96,
      maxWidth: 128,
      renderCell: (params) => {
        const v = params?.row?.strikePrice;
        const display =
          v === undefined || v === null || v === "" ? "" : Number(v).toLocaleString();
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontFamily: "monospace",
              fontSize: "0.8125rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {display}
          </Typography>
        );
      },
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      flex: 0.9,
      minWidth: 140,
      renderCell: (params) => {
        const display = params?.row?.expirationDate || "";
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontFamily: "monospace" }}
          >
            {display || ""}
          </Typography>
        );
      },
    },
    // {
    //   field: "router",
    //   headerName: "Router",
    //   flex: 0.8,
    //   minWidth: 50,
    //   sortable: false,
    //   renderCell: (params) => {
    //     const routerId = params.row.router;
    //     const router = routerId ? routeList.find(route => route.routerId === routerId) : null;
    //     const routerAbbreviation = router ? router.abbreviation : "";
    //     return (
    //       <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
    //         {routerAbbreviation || ""}
    //       </Typography>
    //     );
    //   },
    // },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0,
      width: 110,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => {
        if (isNonSelectableChildRow(params.row, params.id)) {
          return null;
        }
        return (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title={viewType === "deleted" ? "Cannot manage fills for deleted trades" : "Manage Fills"}>
            <span>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => viewType !== "deleted" && params.row.onManageFillsClick?.(params.row)}
                disabled={viewType === "deleted"}
                sx={{
                  "&:hover": {
                    backgroundColor: viewType === "deleted" ? "transparent" : "rgba(156, 39, 176, 0.08)"
                  },
                  opacity: viewType === "deleted" ? 0.5 : 1,
                  cursor: viewType === "deleted" ? "not-allowed" : "pointer"
                }}
              >
                <AssignmentIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={
            viewType === "deleted" ? "Cannot edit deleted trades" :
            params.row.hasProcess ? "Cannot edit - Rebalancer Order" : "Edit Order"
          }>
            <span>
              <IconButton
                size="small"
                color={viewType === "deleted" || params.row.hasProcess ? "disabled" : "primary"}
                onClick={() => viewType !== "deleted" && !params.row.hasProcess && params.row.onEditClick?.(params.row)}
                disabled={viewType === "deleted" || params.row.hasProcess}
                sx={{
                  opacity: (viewType === "deleted" || params.row.hasProcess) ? 0.5 : 1,
                  cursor: (viewType === "deleted" || params.row.hasProcess) ? "not-allowed" : "pointer"
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        );
      },
    },
  ];

    return defs.map((col) =>
      col.sortable === false
        ? col
        : { ...col, getSortComparator: getTreeOrderSortComparator }
    );
  }, [filterKey, theme, flatChildView, viewType, getTreeOrderSortComparator]);

  const gridMinWidth = columns.reduce(
    (sum, col) => sum + (Number(col.width) || Number(col.minWidth) || 100),
    50 // checkbox column
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <DataGrid
        apiRef={gridApiRef}
        rows={flattenedRows}
        columns={columns}
        getRowId={(row) => row.id}
        filterModel={gridFilterModel}
        onFilterModelChange={handleFilterModelChange}
        checkboxSelection
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[100, 200, 500, 1000, 2000, 5000]}
        getRowHeight={() => 30}
        isRowSelectable={(params) =>
          !isNonSelectableChildRow(params.row, params.id)
        }
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={rowSelectionModel}
        rowHeight={30}
        filterMode="client"
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        disableColumnMenu={false}
        disableColumnResize={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
        showToolbar
        onColumnVisibilityModelChange={(model) => {
          if (onColumnStateChange) {
            const hasHidden = Object.values(model).some((visible) => !visible);
            onColumnStateChange(hasHidden);
          }
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          border: 0,
          "--DataGrid-rowBorderColor": theme.palette.divider,
          ".MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.mode === "dark" ? "#121212" : undefined,
          },
          "& .MuiDataGrid-toolbarContainer": {
            minHeight: "40px",
            padding: "2px 8px",
            backgroundColor: theme.palette.mode === "dark" ? "#121212" : theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-main": {
            width: "100%",
            overflow: "hidden",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflow: "auto !important",
            backgroundColor: theme.palette.mode === "dark" ? "#121212" : undefined,
          },
          "& .MuiDataGrid-virtualScrollerContent": {
            minWidth: `${gridMinWidth}px !important`,
          },
          // Gap between header and first row (row maxHeight was clipping border/margin)
          "& .MuiDataGrid-virtualScrollerRenderZone": {
            paddingTop: "8px !important",
          },
          "& .MuiDataGrid-columnHeadersInner": {
            minWidth: `${gridMinWidth}px !important`,
          },
          "& .MuiDataGrid-columnHeaders": {
            minHeight: "48px !important",
            maxHeight: "48px !important",
            zIndex: 3,
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e1e1e" : theme.palette.grey[100],
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "4px 8px",
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-row": {
            minHeight: "30px !important",
            maxHeight: "30px !important",
            backgroundColor: theme.palette.mode === "dark" ? "#121212" : undefined,
            borderBottom: "none",
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" ? "#202020" : undefined,
            },
            "&.highlight": {
              backgroundColor: `${theme.palette.warning.light} !important`,
              "& .MuiDataGrid-cell": {
                backgroundColor: `${theme.palette.warning.light} !important`,
              },
            },
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            padding: "0 8px !important",
            fontSize: "0.8125rem",
            borderTop: "none",
            borderBottom: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            minHeight: "30px !important",
            maxHeight: "30px !important",
            lineHeight: 1.15,
            "& .MuiChip-root": {
              height: "22px",
              maxHeight: "22px",
            },
            "& .MuiTypography-root": {
              lineHeight: 1.15,
            },
          },
          "& .MuiDataGrid-cell.filled-percent-cell": {
            padding: "1px 2px !important",
            display: "flex",
            alignItems: "stretch",
            "& > *": {
              flex: 1,
              width: "100%",
              height: "100% !important",
              alignSelf: "stretch",
              borderRadius: "1px",
            },
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
        }}
      />
    </Box>
  );
});

export default TradeBlotterTable; 